import { CommonModule } from '@angular/common';
import { Component, Input } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { AdviceTodayResponse } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@dto/responses/advice/advice-today.response';
import { AdviceTrigger } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@enums/advice-trigger.enum';
import { Router } from '@angular/router';
import { WillyChatService } from '../../../@commons/@services/front/willy-chat.service';
import routesConstants from '../../../@commons/constants/routes.constants';
import { LocationLabelPipe } from '../../../@commons/pipes/location-label.pipe';

/**
 * Ce que l'accueil montre, et ce qu'il laisse aux écrans spécialisés.
 *
 * Trois conseils et deux récoltes tiennent dans un écran sans défilement. Au
 * delà, l'accueil redevient une liste, et le Potager existe pour ça.
 */
const MAX_ADVICE_ROWS = 3;
const MAX_HARVEST_ROWS = 2;

interface ChipStyle {
  background: string;
  ink: string;
  emoji: string;
}

const CHIP_STYLES: Record<string, ChipStyle> = {
  [AdviceTrigger.FROST]: { background: '#E0F2FE', ink: '#075985', emoji: '❄️' },
  [AdviceTrigger.HEAVY_RAIN]: { background: '#E1EDFE', ink: '#1A4166', emoji: '🌧️' },
  [AdviceTrigger.HEATWAVE]: { background: '#FEF3C7', ink: '#92400E', emoji: '☀️' },
  [AdviceTrigger.DRY_SPELL]: { background: '#FEF9C3', ink: '#854D0E', emoji: '💧' },
  [AdviceTrigger.STRONG_WIND]: { background: '#F1F5F9', ink: '#475569', emoji: '💨' },
  [AdviceTrigger.LUNAR_ROOT]: { background: '#E4E1FE', ink: '#261E77', emoji: '🌙' },
  [AdviceTrigger.LUNAR_LEAF]: { background: '#E4E1FE', ink: '#261E77', emoji: '🌙' },
  [AdviceTrigger.LUNAR_FLOWER]: { background: '#E4E1FE', ink: '#261E77', emoji: '🌙' },
  [AdviceTrigger.LUNAR_FRUIT]: { background: '#E4E1FE', ink: '#261E77', emoji: '🌙' },
};

/**
 * « Aujourd'hui dans ton potager » : le bloc qui répond à « je fais quoi ? ».
 *
 * Il ne déclenche aucun appel : tout vient du `advice/today` que l'accueil
 * charge déjà pour les tuiles de contexte.
 *
 * Deux natures de lignes, et l'ordre n'est pas neutre. Les récoltes d'abord :
 * elles sont certaines — la fenêtre est ouverte, le geste ne se discute pas —
 * et ce sont elles qui donnent envie de sortir. Les conseils ensuite, qui
 * demandent de la lecture.
 *
 * Aucun verbe n'est inventé sur les conseils : le texte vient du modèle, et
 * lui coller un « Arroser » en titre risquerait de contredire ce qu'il dit
 * juste en dessous. Seules les récoltes portent un verbe, parce que là il n'y
 * a pas d'ambiguïté.
 */
@Component({
  selector: 'vw-garden-advices-widget',
  standalone: true,
  imports: [CommonModule, TranslateModule, LocationLabelPipe],
  templateUrl: './garden-advices-widget.component.html',
})
export class GardenAdvicesWidgetComponent {
  @Input() advice: AdviceTodayResponse | null = null;

  constructor(
    private willyChatService: WillyChatService,
    private router: Router,
  ) {}

  /** La récolte se fait au potager : c'est là qu'on envoie. */
  openGarden(): void {
    void this.router.navigate([routesConstants.GARDEN_LIST]);
  }

  get cards() {
    return (this.advice?.advices ?? []).slice(0, MAX_ADVICE_ROWS);
  }

  /** Ce qui est mûr aujourd'hui, et qu'aucun autre écran de l'accueil ne dit. */
  get harvests() {
    return (this.advice?.harvestSuggestions ?? []).slice(0, MAX_HARVEST_ROWS);
  }

  /**
   * Le déclencheur complet correspondant à une carte.
   *
   * La carte ne porte que son code ; les valeurs mesurées (température,
   * millimètres) vivent sur le déclencheur. C'est lui qui permet d'écrire
   * « Forte chaleur (37°) » sous le verbe, avec les mêmes mots que la tuile.
   */
  triggerOf(code: string) {
    return (this.advice?.triggers ?? []).find((t) => t.trigger === code) ?? {};
  }

  /** Le compte annoncé sous le titre : ce qui est réellement affiché. */
  get actionCount(): number {
    return this.harvests.length + this.cards.length;
  }

  /**
   * Conseils dépliés, par clé de carte.
   *
   * Le texte du modèle fait un paragraphe : trois d'affilée transforment le
   * briefing en mur de lecture, et c'est justement l'ecart le plus visible
   * avec la maquette. On en montre deux lignes, et la personne deroule ce qui
   * l'interesse. Tronquer sans possibilité de lire la suite reviendrait a
   * jeter le conseil.
   */
  private readonly expanded = new Set<string>();

  cardKey(card: { plantUid: string; trigger: string }): string {
    return `${card.plantUid}:${card.trigger}`;
  }

  isExpanded(card: { plantUid: string; trigger: string }): boolean {
    return this.expanded.has(this.cardKey(card));
  }

  toggle(card: { plantUid: string; trigger: string }): void {
    const key = this.cardKey(card);
    if (this.expanded.has(key)) {
      this.expanded.delete(key);
    } else {
      this.expanded.add(key);
    }
  }

  chipStyle(trigger: string): ChipStyle {
    return CHIP_STYLES[trigger] ?? { background: '#F5F5F5', ink: '#3c4a45', emoji: '🌱' };
  }

  askWilly() {
    this.willyChatService.openChat();
  }
}
